import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { SEO_PAGES } from '../lib/site';
import { getEvidenceById } from '../lib/evidence';

const page = SEO_PAGES.find((p) => p.path === '/mini-exame-do-estado-mental');
const brucki = getEvidenceById('brucki-2003');

const ANSWER =
  'O Mini Exame do Estado Mental (MEEM), também conhecido como Mini Mental ou MMSE, é um instrumento breve de rastreio cognitivo. Ele organiza tarefas de orientação, memória, atenção/cálculo, linguagem e habilidade visuoconstrutiva em um escore total, que deve ser interpretado com escolaridade, contexto clínico e condições da aplicação.';

const RELATED = [
  { to: '/#calculadora', label: 'Mini Mental online', note: 'some a pontuação já obtida por domínios.' },
  { to: '/interpretacao-dos-resultados', label: 'Pontuação e interpretação', note: 'entenda resultado, pontos de corte e limitações.' },
  { to: '/escolaridade', label: 'Mini Mental por escolaridade', note: 'compare referências brasileiras sem confundir mediana com corte.' },
  { to: '/mini-mental-pdf', label: 'Mini Mental PDF', note: 'acesse a referência brasileira original e veja direitos de uso.' },
  { to: '/como-aplicar', label: 'Como aplicar', note: 'revise princípios de aplicação padronizada.' }
];

const REFERENCES = [`${brucki.citation} DOI: ${brucki.primarySource.value}.`];

export default function MiniExameDoEstadoMental() {
  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="o-que-e-meem">
        <h2 id="o-que-e-meem" className="text-2xl mb-5">O que é o Mini Exame do Estado Mental?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          O <strong>Mini Exame do Estado Mental (MEEM)</strong> é a adaptação amplamente usada no Brasil do <em>Mini-Mental State Examination</em> (MMSE). Na prática, “MEEM”, “Mini Mental” e “Mini Exame do Estado Mental” aparecem como nomes para a mesma família de instrumento de rastreio cognitivo.
        </p>
        <p className="text-[var(--text-muted)]">
          O objetivo é produzir uma medida breve e estruturada do desempenho cognitivo. O escore ajuda a organizar o rastreio, mas não estabelece sozinho diagnóstico de demência, Alzheimer ou qualquer etiologia específica.
        </p>
      </section>

      <section aria-labelledby="o-que-avalia">
        <h2 id="o-que-avalia" className="text-2xl mb-5">O que o Mini Mental avalia?</h2>
        <p className="text-[var(--text-muted)] mb-4">A avaliação reúne tarefas distribuídas em domínios como:</p>
        <ul className="space-y-3 text-[var(--text-muted)] list-disc pl-5">
          <li>orientação temporal e espacial;</li>
          <li>registro e evocação de informações;</li>
          <li>atenção e cálculo;</li>
          <li>linguagem;</li>
          <li>execução de comandos;</li>
          <li>habilidade visuoconstrutiva.</li>
        </ul>
        <p className="text-[var(--text-muted)] mt-4">
          A composição exata, a redação das instruções e o material de aplicação devem seguir a versão autorizada utilizada pelo profissional. Este site não reproduz o roteiro integral do instrumento.
        </p>
      </section>

      <section aria-labelledby="pontuacao-meem">
        <h2 id="pontuacao-meem" className="text-2xl mb-5">Como funciona a pontuação?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          O MEEM tradicional produz um escore total de até 30 pontos. A leitura clínica, porém, não deve transformar esse número em uma classificação automática. Escolaridade, idade, idioma, déficits sensoriais, estado agudo e padronização da aplicação podem alterar o desempenho.
        </p>
        <p className="text-[var(--text-muted)]">
          Se você já tem os pontos por domínio, use a <Link to="/#calculadora" className=" text-[var(--primary)] hover:underline">calculadora Mini Mental online</Link>. Depois, consulte <Link to="/interpretacao-dos-resultados" className=" text-[var(--primary)] hover:underline">pontuação, resultado e pontos de corte</Link>.
        </p>
      </section>

      <section aria-labelledby="referencia-brasileira">
        <h2 id="referencia-brasileira" className="text-2xl mb-5">Qual é a referência brasileira?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Brucki et al. (2003) é uma das referências brasileiras centrais para o uso do MEEM. O estudo avaliou {brucki.sampleSize} indivíduos saudáveis e descreveu medianas diferentes conforme a escolaridade.
        </p>
        <p className="text-[var(--text-muted)]">
          Essas medianas não devem ser chamadas automaticamente de “pontos de corte”. Para ver os valores e a diferença entre referência descritiva e limiar de rastreio, consulte <Link to="/escolaridade" className=" text-[var(--primary)] hover:underline">Mini Mental por escolaridade</Link> e <Link to="/pontos-de-corte" className=" text-[var(--primary)] hover:underline">pontos de corte</Link>.
        </p>
      </section>

      <section aria-labelledby="quando-usar">
        <h2 id="quando-usar" className="text-2xl mb-5">Para que serve — e para que não serve?</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="p-5 border border-[var(--border)] rounded-2xl bg-[var(--surface)]">
            <h3 className="font-bold text-[var(--primary)] mb-3">Serve para</h3>
            <ul className="space-y-2 text-sm text-[var(--text-muted)] list-disc pl-5">
              <li>rastreio cognitivo breve;</li>
              <li>documentar desempenho em uma avaliação;</li>
              <li>compor avaliação clínica mais ampla;</li>
              <li>acompanhar evolução quando a reaplicação é comparável.</li>
            </ul>
          </div>
          <div className="p-5 border border-[var(--border)] rounded-2xl bg-[var(--surface)]">
            <h3 className="font-bold text-[var(--primary)] mb-3">Não serve sozinho para</h3>
            <ul className="space-y-2 text-sm text-[var(--text-muted)] list-disc pl-5">
              <li>diagnosticar doença de Alzheimer;</li>
              <li>definir etiologia do comprometimento;</li>
              <li>substituir história e funcionalidade;</li>
              <li>ignorar escolaridade e condições da aplicação.</li>
            </ul>
          </div>
        </div>
      </section>
    </ArticleLayout>
  );
}
